<script lang="ts">
  import SuccessToast from "@/components/results/success-toast.svelte";
  import { client, trpcTryCatch } from "@/helpers/trpc/trpc-client";
  import { user } from "@/stores/user";
  import { quintOut } from "svelte/easing";
  import { fly } from "svelte/transition";
  import { toast } from "svoast";
  import i18n from "@/stores/i18n";

  import { Button } from "@/components/ui/button";
  import * as Dialog from "@/components/ui/dialog";
  import { Image } from "@/components/ui/image";
  import { Input } from "@/components/ui/input";
  import { Label } from "@/components/ui/label";
  import { Separator } from "@/components/ui/separator";
  import { Typography } from "@/components/ui/typography";

  import Anonymous from "@/images/anonymous.jpg?raw";
  import LoadingIcon from "@/images/loading.svg";
  import UploadIcon from "@/images/upload_2.svg";

  const t = (key: string, options?: any): string | object =>
    $i18n.t(key, { ns: "common", ...options });

  interface Props {
    open?: boolean;
    onOpenChange: () => any;
  }

  let { open = $bindable(false), onOpenChange }: Props = $props();

  let name = $state($user?.name ?? "");
  let uploadedImageURL: string | null = $state(null);

  let files: FileList | undefined = $state();
  let previewImg: string | null = $state(null);

  let saving = $state(false);

  $effect.pre(() => {
    if (files && files[0]) {
      const reader = new FileReader();
      reader.onload = function (e) {
        previewImg = e.target?.result as string;
      };
      reader.readAsDataURL(files[0]);
    }
  });

  const saveProfile = async () => {
    saving = true;

    if (files?.[0]) {
      const uploadDetails = await client.user.userAvatarPresignedUrl.query();
      const { url, fileName } = uploadDetails;

      const response = await fetch(url, {
        method: "PUT",
        headers: {
          "Content-Type": files[0].type,
          "x-amz-acl": "public-read"
        },
        body: files[0]
      });

      if (response.ok) {
        uploadedImageURL = url.split("?")[0]!;
      }
    }

    if (!$user) {
      saving = false;
      return;
    }
    trpcTryCatch(async () => {
      await client.user.updateProfile.mutate({
        name,
        imgURL: uploadedImageURL ?? undefined
      });

      user.set(await client.user.getUser.query());

      toast.success(t("profile.update_success") as string, {
        component: [SuccessToast, {}]
      });
    });
    saving = false;
  };
</script>

<Dialog.Root {open} {onOpenChange}>
  <Dialog.Content
    class="left-[unset] right-3 flex h-[calc(100vh-12px)] !w-[416px] max-w-[92vw] translate-x-[0%] flex-col !rounded-[8px] border border-none border-dark-gray bg-light-black p-[24px]">
    <Dialog.Header>
      <Dialog.Title>
        <Typography variant="lead">{t("profile.edit_title")}</Typography>
      </Dialog.Title>
    </Dialog.Header>
    <Separator class="-ml-[24px] w-[calc(100%+48px)]" />

    <div
      class="-mx-6 -my-4 flex h-full flex-col overflow-y-scroll bg-theme-black p-6">
      <div class="flex flex-col overflow-y-scroll">
        <div class="mt-6 flex w-full flex-col gap-3">
          <Label for="name" class="ml-[2px]">
            <Typography color="light_gray" variant="large">
              {t("profile.picture_label")}
            </Typography>
          </Label>
          <div
            class="flex items-center gap-4 rounded-[8px] border border-dark-gray bg-light-black p-4">
            {#if previewImg}
              <img
                class="h-12 w-12 rounded-[8px] object-cover"
                alt={t("profile.avatar_alt") as string}
                src={previewImg} />
            {:else if $user?.image}
              <img
                class="h-12 w-12 rounded-[8px] object-cover"
                alt={t("profile.avatar_alt") as string}
                src={$user?.image} />
            {:else}
              <Image
                class="h-12 w-12 rounded-[8px]"
                alt={t("profile.avatar_alt") as string}
                src={Anonymous} />
            {/if}
            <div class="flex flex-1 items-center justify-end gap-2">
              <label for="img" class="">
                <div
                  class="flex h-10 w-10 items-center justify-center rounded-[8px] border border-dark-gray bg-blue-gray p-0 hover:cursor-pointer">
                  <Image
                    src={UploadIcon}
                    alt={t("profile.upload_icon_alt") as string}
                    class="h-5 w-5" />
                </div>
              </label>
              <input
                bind:files
                type="file"
                name="img"
                id="img"
                class="sr-only"
                accept="image/*" />
            </div>
          </div>
        </div>
        <div class="mt-6 flex w-full flex-col gap-3">
          <Label for="name" class="ml-[2px]">
            <Typography color="light_gray" variant="large">
              {t("profile.name_label")}
            </Typography>
          </Label>
          <Input
            bind:value={name}
            type="text"
            id="name"
            placeholder={t("profile.name_placeholder") as string}
            class="max-w-[444px] rounded-[8px] border-none bg-blue-gray p-6 text-sm font-semibold leading-6 text-light-gray placeholder:text-medium-gray focus-visible:ring-0 focus-visible:ring-offset-0" />
          <Typography class="ml-[2px]" variant="small" color="muted">
            {t("profile.name_hint")}
          </Typography>
        </div>
        <div class="mt-6 flex w-full flex-col gap-3">
          <Label for="email" class="ml-[2px]">
            <Typography color="light_gray" variant="large">
              {t("profile.email_label")}
            </Typography>
          </Label>
          <Input
            disabled
            value={$user?.email}
            type="text"
            id="email"
            placeholder={t("profile.email_placeholder") as string}
            class="max-w-[444px] rounded-[8px] border-none bg-blue-gray p-6 text-sm font-semibold leading-6 text-light-gray placeholder:text-medium-gray focus-visible:ring-0 focus-visible:ring-offset-0" />
          <Typography class="ml-[2px]" variant="small" color="muted">
            {t("profile.email_hint")}
          </Typography>
        </div>
      </div>
    </div>

    <Separator class="-ml-[24px] w-[calc(100%+48px)]" />
    <div class="inline-flex items-center justify-end gap-4 bg-light-black">
      <Button
        typographyVariant="p"
        typographyWeight="medium"
        onclick={() => (open = false)}
        variant="secondary">
        {t("profile.cancel_button")}
      </Button>

      <Button onclick={saveProfile} variant="default" disabled={saving}>
        <div class="flex items-center gap-2">
          {#if saving}
            <Image
              src={LoadingIcon}
              alt={t("profile.loading_icon_alt") as string}
              class="h-5 w-5 animate-spin" />
          {/if}
          {t("profile.save_button")}
        </div>
      </Button>
    </div>
  </Dialog.Content>
</Dialog.Root>

<style>
  ::-webkit-scrollbar {
    width: 5px;
  }

  ::-webkit-scrollbar-track {
    background: transparent;
  }

  ::-webkit-scrollbar-thumb {
    background: #3a3a3a;
    border-radius: 10px;
  }
</style>
